import React, { useState, useEffect } from "react";
import "../../css/HRPortal.css";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import host from "../../global";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const Dashboard = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jobPositionsCount, setJobPositionsCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({
    Pending: 0,
    "In Progress": 0,
    Completed: 0,
    Unknown: 0,
    Total: 0,
  });
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const navigate = useNavigate();
  const accessToken = Cookies.get("accessToken");
  const MAX_INTERVIEWS = 6;

  function convertToISO(date) {
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    const localISOTime = new Date(
      date.getTime() - timezoneOffset
    ).toISOString();
    return localISOTime.slice(0, -1);
  }

  const convertToLocalTime = (utcTimeString) => {
    const utcDate = new Date(utcTimeString);
    const localTime = new Intl.DateTimeFormat("default", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(utcDate);
    return localTime;
  };

  const handlePieClick = (data, index) => {
    const statusMapping = {
      Completed: "Completed",
      "In Progress": "In Progress",
      "Not Started": "Pending",
    };
    const status = statusMapping[data.name];
    if (status) {
      navigate("/hr/candidatereports", {
        state: {
          combined_status: status,
          page: 1,
          job_position: "All",
        },
      });
    }
  };

  const handleLegendClick = (entry) => {
    const statusMapping = {
      Completed: "Completed",
      "In Progress": "In Progress",
      "Not Started": "Pending",
    };
    const status = statusMapping[entry.value];
    if (status) {
      navigate("/hr/candidatereports", {
        state: {
          combined_status: status,
          page: 1,
          job_position: "All",
        },
      });
    }
  };
  const renderCustomLegend = ({ payload }) => {
    return (
      <div className="flex justify-center items-center gap-3 pt-3 mt-0">
        {payload.map((entry, index) => (
          <div
            key={`candidate-legend-${index}`}
            className="flex items-center cursor-pointer hover:opacity-80"
            onClick={() => handleLegendClick(entry)}
          >
            <div
              className="w-2 h-2 mr-1 rounded-sm"
              style={{ backgroundColor: entry.color }}
            ></div>
            <span className="text-gray-300 text-xs font-light">
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const handleDateChange = async (date) => {
    setSelectedDate(date);
  };

  const InterviewCard = ({ interview }) => (
    <div
      className="py-[10px] flex justify-between items-center rounded-2xl shadow-sm cursor-pointer"
      onClick={() => navigate(`/hr/evaluationpage/${interview.candidate_id}`)}
    >
      <div className="flex flex-col w-[150px]">
        <p
          className="text-md font-semibold text-gray-300 truncate"
          title={interview.candidate_name || "Unknown"}
        >
          {interview.candidate_name || "Unknown"}
        </p>
        <p
          className="text-sm text-gray-400 truncate"
          title={interview.job_role || "Unknown"}
        >
          {interview.job_role || "Unknown"}
        </p>
      </div>
      <div className="flex items-center">
        <div className="h-12 w-[1px] bg-gray-300 mx-3"></div>
        <p className="text-sm text-gray-400 font-semibold">
          {convertToLocalTime(interview.interview_time)}
        </p>
      </div>
    </div>
  );

  const renderInterviewCards = () => {
    const displayInterviews = interviews.slice(0, MAX_INTERVIEWS);

    return (
      <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-[15px] w-full">
        {displayInterviews.map((interview, index) => (
          <div key={index} className="flex justify-center">
            <InterviewCard interview={interview} />
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    const fetchScheduledInterviews = async () => {
      setLoading(true);
      const isoDate = convertToISO(selectedDate) + "Z";
      try {
        const response = await fetch(
          `${host}/scheduled_interviews?date=${isoDate}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setInterviews(data);
      } catch (error) {
        if (error.response) {
          const status = error.response.status;
          const detail = error.response.data.detail;
          if (status === 401) {
            await new Promise((resolve) => {
              Cookies.remove("accessToken");
              resolve();
            });
            toast.error(`Unauthorized: ${detail}`);
          } else if (status === 404) {
            toast.warning(`Not Found: ${detail}`);
          } else {
            toast.error(`Error: ${detail}`);
          }
        } else {
          toast.error("An unexpected error occurred. Please try again.");
        }
        setInterviews([]);
      } finally {
        setLoading(false);
      }
    };
    const fetchMetricsData = async () => {
      setIsLoadingMetrics(true);
      try {
        const statsResponse = await fetch(
          `${host}/orgadmin/get-dashboard-stats`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        if (!statsResponse.ok)
          throw new Error(`HTTP error! status: ${statsResponse.status}`);
        console.log(statsResponse);
        const statsData = await statsResponse.json();
        setStatusCounts(statsData.candidate_status_counts);

        const jobResponse = await fetch(`${host}/getjobpositionsdata`, {
          method: "GET",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!jobResponse.ok)
          throw new Error(`HTTP error! status: ${jobResponse.status}`);
        const jobData = await jobResponse.json();
        setJobPositionsCount(jobData.length);
      } catch (error) {
        handleFetchError(error, setStatusCounts, {
          Pending: 0,
          "In Progress": 0,
          Completed: 0,
          Unknown: 0,
          Total: 0,
        });
        setJobPositionsCount(0);
      } finally {
        setIsLoadingMetrics(false);
      }
    };

    const handleFetchError = (error, setData, defaultValue) => {
      if (error.response) {
        const {
          status,
          data: { detail },
        } = error.response;
        if (status === 401) {
          Cookies.remove("accessToken");
          toast.error(`Unauthorized: ${detail}`);
        } else if (status === 404) {
          toast.warning(`Not Found: ${detail}`);
        } else {
          toast.error(`Error: ${detail}`);
        }
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
      setData(defaultValue);
    };

    fetchScheduledInterviews();
    fetchMetricsData();
  }, [selectedDate, accessToken]);

  const hasCandidateData =
    statusCounts.Completed > 0 ||
    statusCounts["In Progress"] > 0 ||
    statusCounts.Pending > 0;

  return (
    <div className="p-8 h-full overflow-auto no-scrollbar">
      <div className="flex flex-row justify-start items-center mb-8 gap-10">
        <div
          className="flex flex-col justify-center items-center card cursor-pointer"
          onClick={() => navigate(`/hr/checkresume`)}
        >
          <div className="text">
            <img
              src="/analyzeresumehover.png"
              alt="Card Icon"
              className="w-10 h-auto max-w-xs mx-auto"
            />
            <p className="subtitle whitespace-nowrap text-base font-light dark:text-gray-200 text-textPrimary ">
              Analyze Resume
            </p>
          </div>
        </div>
        {/* <div
          className="flex flex-col justify-center items-center card cursor-pointer"
          onClick={() => navigate(`/hr/BulkResume`)}
        >
          <div className="text">
            <img
              src="/bulkresume.png"
              alt="Card Icon"
              className="w-16 h-auto max-w-xs mx-auto"
            />
            <p className="subtitle">Upload Bulk Resume</p>
          </div>
        </div> */}
      </div>
      <div className="mt-10 mb-10">
        <div className="metrics-container">
          {/* Candidate Stats Pie Chart*/}
          <div
            className="metric-card dark:glass-effect bg-bgSecondary rounded-xl flex flex-col cursor-pointer"
            onClick={() => navigate("/hr/candidatereports")}
          >
            <h3 className="ml-4 mt-4 dark:text-gray-300 text-textPrimary font-light">
              Assessment Status
            </h3>
            {isLoadingMetrics ? (
              <div className="flex-grow flex flex-col items-center justify-center p-4">
                <div className="w-32 h-32 dark:bg-gray-500/20 bg-gray-200 rounded-full animate-pulse mb-8"></div>
                <div className="flex justify-center items-center gap-3 w-full">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="flex items-center">
                      <div className="w-2 h-2 mr-1 rounded-sm dark:bg-gray-500/20 bg-gray-200 animate-pulse"></div>
                      <div className="h-3 w-16 dark:bg-gray-500/20 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  ))}
                </div>
              </div>
            ) : !hasCandidateData ? (
              <div className="flex flex-col h-full">
                <div className="flex-grow"></div>
                <div className="flex justify-center pb-6">
                  <span className="text-gray-400 text-center text-xs">
                    Looks like there's no data just yet
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-grow flex items-center justify-center pb-0">
                  <ResponsiveContainer width="100%" height={170}>
                    <PieChart>
                      {(() => {
                        const candidateData = [
                          {
                            name: "Completed",
                            value: statusCounts.Completed,
                            color: "#9500D3",
                          },
                          {
                            name: "In Progress",
                            value: statusCounts["In Progress"],
                            color: "#F072BA",
                          },
                          {
                            name: "Not Started",
                            value: statusCounts.Pending,
                            color: "#B282F9",
                          },
                        ].filter((item) => item.value > 0);

                        return (
                          <Pie
                            data={candidateData}
                            cx="50%"
                            cy="50%"
                            innerRadius={35}
                            outerRadius={70}
                            fill="#8884d8"
                            dataKey="value"
                            isAnimationActive={false}
                            paddingAngle={0}
                            onClick={handlePieClick}
                            style={{ cursor: "pointer" }}
                            stroke="none"
                          >
                            {candidateData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                        );
                      })()}
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "rgba(0, 0, 0, 0.8)",
                          border: "none",
                          borderRadius: "4px",
                          color: "white",
                        }}
                        itemStyle={{ color: "white" }}
                      />
                      <Legend content={renderCustomLegend} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>

          {/* Job Descriptions Count */}
          <div className="metric-card dark:glass-effect bg-bgSecondary p-4 rounded-xl flex flex-col">
            <h3 className="dark:text-gray-300 text-textPrimary font-light">
              Created Job Roles
            </h3>
            {isLoadingMetrics ? (
              <div className="flex-grow flex items-center justify-center">
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="h-10 w-16 dark:bg-gray-500/20 bg-gray-200 rounded animate-pulse mb-2"></div>
                  <div className="h-3 w-24 dark:bg-gray-500/20 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            ) : (
              <div className="flex-grow flex items-center justify-center">
                <div className="flex flex-col items-center justify-center text-center">
                  <span className="text-3xl font-bold text-blue-500">
                    {jobPositionsCount}
                  </span>
                  <span className="text-xs font-light text-center text-[rgba(240,248,255,0.691)] block mt-2">
                    Active Positions
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* <div
        className="glass-metric-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
          gap: "1rem",
          marginTop: "2rem",
          padding: "20px",
        }}
      >
        {["Total Test Submissions", "Passed Candidates", "Rejected Candidates", "Shortlisted Candidates"].map(
          (title, index) => (
            <Card
              key={index}
              style={{
                backdropFilter: "blur(10px)",
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "16px",
              }}
            >
              <CardHeader
                avatar={<BusinessCenterRoundedIcon />}
                title={<Typography>{title}</Typography>}
              />
              <CardContent>
                <Typography variant="h4" style={{ textAlign: "center" }}>
                  {"0" + (index + 1)}
                </Typography>
              </CardContent>
            </Card>
          )
        )}
      </div> */}
      {/* <div className="cardPattern flex flex-col gap-8 p-4 md:p-6 mt-8">
        <h3 className="mt-2 font-semibold text-gray-300 text-lg ml-4">
          Interviews Scheduled
        </h3>
        <div className="flex flex-col lg:flex-row gap-2">
          <div className="w-full lg:w-[35%] flex justify-start">
            <Calendar
              onChange={handleDateChange}
              value={selectedDate}
              className="w-full max-w-[450px] border-1"
            />
          </div>

          <div className="pl-2 w-full lg:w-[65%] flex flex-col">
            <div className="flex-grow">
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-[15px] w-full">
                  {[...Array(6)].map((_, index) => (
                    <div className="p-4 rounded-2xl shadow-sm animate-pulse">
                      <div className="flex justify-between items-center">
                        <div className="flex flex-col space-y-2 w-2/3">
                          <div className="h-4 bg-gray-500 rounded w-3/4"></div>
                          <div className="h-3 bg-gray-500 rounded w-1/2"></div>
                        </div>
                        <div className="h-8 w-[1px] bg-gray-500 mx-3"></div>
                        <div className="h-3 bg-gray-500 rounded w-1/4"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : interviews.length > 0 ? (
                renderInterviewCards()
              ) : (
                <p className="text-center text-gray-500">
                  No interviews scheduled
                </p>
              )}
            </div>

            {interviews.length > MAX_INTERVIEWS && (
              <div className="flex justify-center mt-4">
                <button
                  onClick={() => navigate("/hr/interviews")}
                  className="button"
                >
                  <span>View All Interviews</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div> */}
    </div>
  );
};
export default Dashboard;
